/* eslint-disable no-unused-vars */
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { createUserWithEmailAndPassword, getAuth, signInWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth'
import { auth, firebaseConfig, firestore } from '../../config/firebase'
import { setDoc, doc, getDoc } from 'firebase/firestore'
import { manualAddNewUser } from '../users/usersSlice'
import { deleteApp, initializeApp } from 'firebase/app'
import { deleteCreatedUser, getProfile } from './helper'
// import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage'

export const checkLogin = createAsyncThunk('auth/checkLogin', async (userlogin) => {
  if (!userlogin) {
    throw new Error('Need to login first')
  } else {
    return await getProfile(userlogin)
  }
})

export const login = createAsyncThunk('auth/login', async ({ email, password }) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password)
    return await getProfile(userCredential.user)
  } catch {
    return {}
  }
})

export const logout = createAsyncThunk('auth/logout', async () => {
  await signOut(auth)
})

export const signUp = createAsyncThunk(
  'auth/signup',
  async (
    {
      email,
      password,
      displayName,
      dob,
      phone,
      nid,
      close,
      signature,
      role
    },
    ThunkAPI
  ) => {
    const singupInstance = initializeApp(firebaseConfig, 'signupInstance')
    const authInstance = getAuth(singupInstance)
    try {
      const userCredential = await createUserWithEmailAndPassword(authInstance, email, password)
      const { user } = userCredential
      let photoURL = ''
      await updateProfile(userCredential.user, {
        displayName: displayName || 'No name',
        photoURL
      })
      const userRef = doc(firestore, 'users', user.uid)
      const optimisticData = {
        user_id: user.uid,
        email: user.email,
        displayName,
        photoURL,
        dob,
        role: role || 'standard',
        phone,
        nid,
        signature
      }
      try {
        await setDoc(userRef, optimisticData)
        return ThunkAPI.dispatch(
          manualAddNewUser({
            ...optimisticData,
            id: user.uid
          })
        )
      } catch (error) {
        await deleteCreatedUser(email, password) // Deletes the newly created auth user
        throw error // Re-throw to be caught by outer catch
      }
    } catch (e) {
      console.log('error', e)
      return {}
    } finally {
      try {
        await deleteApp(singupInstance)
      } catch (deleteError) {
        // Ignore errors if the app is already deleted
        if (!deleteError.message.includes('already deleted')) {
          console.error('Error deleting Firebase instance:', deleteError)
        }
      }
      close && close()
    }
  }
)

export const updateUser = createAsyncThunk(
  'auth/updateUser',
  async ({ email, displayName, dob, phone, nid, close, id, signature, color, role, ...rest }, ThunkAPI) => {
    try {
      const userRef = doc(firestore, 'users', id)
      const optimisticData = {
        user_id: id,
        email: email,
        displayName,
        dob,
        phone,
        nid,
        signature,
        color,
        role: role || 'standard'
      }
      await setDoc(userRef, optimisticData, { merge: true })
      return ThunkAPI.dispatch(
        manualAddNewUser({
          ...rest,
          ...optimisticData,
          id: id
        })
      )
    } catch {
      return {}
    } finally {
      close && close()
    }
  }
)

const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    loading: true,
    error: null
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(checkLogin.fulfilled, (state, action) => {
        state.user = action.payload
        state.loading = false
        state.error = false
      })
      .addCase(checkLogin.rejected, (state, action) => {
        state.loading = false
        state.error = true
      })
      // logins
      .addCase(login.pending, (state) => {
        state.loading = true
      })
      .addCase(login.fulfilled, (state, action) => {
        state.user = action.payload
        state.loading = false
        state.error = false
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false
        state.error = true
      })
      // logout
      .addCase(logout.pending, (state) => {
        state.loading = true
      })
      .addCase(logout.fulfilled, (state) => {
        state.user = null
      })
  }
})

export const { setUser } = authSlice.actions
export default authSlice.reducer
